"use client";

import { cn } from "@/components/libs/cn";
import { AnimatePresence, motion, type HTMLMotionProps } from "framer-motion";
import { useEffect, useState, type ReactNode } from "react";
import { isE2e } from "@/shared/utils/isE2e";

interface WordRotateProps {
  words: ReactNode[];
  duration?: number;
  framerProps?: HTMLMotionProps<"h1">;
  className?: string;
}

export function WordRotate({
  words,
  duration = 2500,
  framerProps = {
    initial: { opacity: 0, y: -50 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 50 },
    transition: { duration: 0.25, ease: "easeOut" },
  },
  className,
}: WordRotateProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (isE2e()) {
      return;
    }

    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, duration);

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [words, duration]);

  const isE2eEnvironment = isE2e();

  return (
    <div className="overflow-hidden py-2">
      <div className="h-0 overflow-hidden">
        {words.map((word, index) => (
          <div key={index}>{word}</div>
        ))}
      </div>
      {isE2eEnvironment ? (
        <h1 className={cn(className)}>{words[index]}</h1>
      ) : (
        <AnimatePresence mode="popLayout">
          <motion.h1 key={index} className={cn(className)} {...framerProps}>
            {words[index]}
          </motion.h1>
        </AnimatePresence>
      )}
    </div>
  );
}
